import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form, Row, Col, Container, Modal } from "react-bootstrap";
import { Icon, Loader as SemanticUILoader } from "semantic-ui-react";
import axios from "axios";

import CommonModal from "../../components/CommonModal";
import LearnerSelectionTable from "./LearnerSelectionTable";
import { selectUser } from "../../Redux/Actions/enrollmentsAction";

const buildLearnerKey = (row) => {
  if (!row) return "";
  const { user_id, first_name, last_name } = row;
  if (!user_id) return "";
  return `${user_id}:${first_name ?? ""},${last_name ?? ""}`;
};

const FindLearnerModal = ({ show, onClose, onConfirm }) => {
  const baseApiUrl = global.config.baseApiUrl;
  const companyId = global.config.companyId;

  const dispatch = useDispatch();

  const [searchLearner, setSearchLearner] = useState({
    firstName: "",
    lastName: "",
    orgName: "",
    id: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasBeenSearched, setHasBeenSearched] = useState(false);
  const [selectedLearnerKey, setSelectedLearnerKey] = useState("");
  const [showAlert, setShowAlert] = useState({
    noTextAdded: false,
    noUserSelected: false,
  });

  const onChange = (key) => (e) => {
    const searchTerm = e.target.value;
    setSearchLearner((prev) => ({
      ...prev,
      [key]: searchTerm,
    }));
  };

  const enableAlert = (key) => {
    setShowAlert((prev) => ({ ...prev, [key]: true }));
  };

  const hideAlert = (key) => {
    setShowAlert((prev) => ({ ...prev, [key]: false }));
  };

  const onSearch = async () => {
    const { firstName, lastName, orgName, id } = searchLearner;

    if (!firstName && !lastName && !orgName && !id) {
      enableAlert("noTextAdded");
      setRows([]);
      setSelectedLearnerKey("");
      return;
    }

    const searchTerm = `${firstName} ${lastName} ${orgName} ${id}`.trim();

    try {
      setLoading(true);

      const res = await axios.post(`${baseApiUrl}/people`, {
        search_text: searchTerm,
        scanned: "N",
      });

      setRows(res.data.users || []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setSelectedLearnerKey("");
      setHasBeenSearched(true);
      setLoading(false);
    }
  };

  const handleSelect = (row) => {
    const learnerKey = buildLearnerKey(row);
    setSelectedLearnerKey(learnerKey);
    // keep this for legacy consumers of enrollments.user
    dispatch(selectUser(learnerKey));
  };

  const handleConfirm = () => {
    if (!selectedLearnerKey) {
      enableAlert("noUserSelected");
      return;
    }

    if (typeof onConfirm === "function") {
      onConfirm(selectedLearnerKey);
    }

    setSearchLearner({ firstName: "", lastName: "", orgName: "", id: "" });
    setRows([]);
    setHasBeenSearched(false);
    setSelectedLearnerKey("");
    onClose();
  };

  const columns = companyId.includes("ford")
    ? [
        { key: "gpid", label: "GPID (Person Number)" },
        { key: "username", label: "CDSID (Username)" },
        { key: "fullName", label: "Full Name" },
        { key: "email", label: "Email" },
      ]
    : [
        { key: "id", label: "User Id" },
        { key: "username", label: "Username" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "Manager", label: "Manager" },
      ];

  const formattedRows = useMemo(
    () =>
      companyId.includes("ford")
        ? rows.map((u) => ({
            gpid: u.user_id,
            username: u.username,
            fullName: u.name,
            email: u.email,
            ...u,
          }))
        : rows.map((u) => ({
            id: u.user_id,
            username: u.username,
            fullName: `${u.first_name} ${u.last_name}`,
            email: u.email,
            manager: u.is_manager,
            ...u,
          })),
    [companyId, rows],
  );

  return (
    <>
      <CommonModal
        show={show}
        onHide={onClose}
        title={
          <Modal.Title>
            <Icon name="search" /> Find Learner
          </Modal.Title>
        }
        footer={
          <>
            <Button variant="dark" onClick={onClose} disabled={loading}>
              Close
            </Button>
            <Button variant="success" onClick={handleConfirm}>
              Confirm
            </Button>
          </>
        }
        modalProps={{
          size: "lg",
          centered: true,
          backdrop: "static",
          className: "manage-roster-modal find-learner-modal",
        }}
      >
        <Container>
          <Row>
            <Col>
              <p className="mb-3">
                Find a learner by entering all or part of their ID, name, or organization.
              </p>
            </Col>
          </Row>

          <Row className="mb-3 px-3">
            <Col md={3} className="learner-field">
              <Form.Control placeholder="First Name" onChange={onChange("firstName")} />
            </Col>
            <Col md={3} className="learner-field">
              <Form.Control placeholder="Last Name" onChange={onChange("lastName")} />
            </Col>
            <Col md={3} className="learner-field">
              <Form.Control placeholder="Organization Name" onChange={onChange("orgName")} />
            </Col>
            <Col md={2} className="learner-field">
              <Form.Control placeholder="ID" onChange={onChange("id")} />
            </Col>
            <Col md={1} className="px-0">
              <Button
                variant="primary"
                className="w-100 btn standard-btn bold"
                onClick={onSearch}
                disabled={loading}
              >
                Search
              </Button>
            </Col>
          </Row>

          {loading ? (
            <SemanticUILoader active inline="centered">
              Searching...
            </SemanticUILoader>
          ) : hasBeenSearched && formattedRows.length === 0 ? (
            <>
              <hr className="hr w-100 mt-4" />
              <div className="text-center font-italic">
                No results found. Please provide full first name and last name.
              </div>
            </>
          ) : (
            formattedRows.length > 0 && (
              <>
                <hr className="hr w-100 mt-5 mb-4" />
                <LearnerSelectionTable
                  columns={columns}
                  rows={formattedRows}
                  onSelect={handleSelect}
                  selectedKey={selectedLearnerKey}
                  getRowKey={buildLearnerKey}
                />
              </>
            )
          )}
        </Container>
      </CommonModal>

      {/* Alert: no search text */}
      <CommonModal
        show={showAlert.noTextAdded}
        onHide={() => hideAlert("noTextAdded")}
        title={<Modal.Title>Alert</Modal.Title>}
        footer={
          <Button variant="dark" onClick={() => hideAlert("noTextAdded")} className="px-4">
            Ok
          </Button>
        }
        modalProps={{ centered: true, backdrop: false, className: "manage-roster-modal" }}
      >
        Please enter at least one item to search by.
      </CommonModal>

      {/* Alert: no user selected */}
      <CommonModal
        show={showAlert.noUserSelected}
        onHide={() => hideAlert("noUserSelected")}
        title={<Modal.Title>Alert</Modal.Title>}
        footer={
          <Button variant="dark" onClick={() => hideAlert("noUserSelected")} className="px-4">
            Ok
          </Button>
        }
        modalProps={{ centered: true, backdrop: false, className: "manage-roster-modal" }}
      >
        Please select a user before continuing.
      </CommonModal>
    </>
  );
};

export default FindLearnerModal;
